import { createAction, createReducer } from "@reduxjs/toolkit"
import { api } from "./helpers/api"
import { LOADING_INITIAL_STATE, setRequested, setReceived, setFailed } from "modules/loader-watchers/helpers/loading"

// Actions for fetching journal note
const equipmentJournalNoteRequested = createAction("equipmentJournal/noteRequested")
const equipmentJournalNoteReceived = createAction("equipmentJournal/noteReceived")
const equipmentJournalNoteFailed = createAction("equipmentJournal/noteFailed")

// Actions for creating journal note
const equipmentJournalCreateNoteRequested = createAction("equipmentJournal/createNoteRequested")
const equipmentJournalCreateNoteReceived = createAction("equipmentJournal/createNoteReceived")
const equipmentJournalCreateNoteFailed = createAction("equipmentJournal/createNoteFailed")

// Actions for updating journal note
const equipmentJournalUpdateNoteRequested = createAction("equipmentJournal/updateNoteRequested")
const equipmentJournalUpdateNoteReceived = createAction("equipmentJournal/updateNoteReceived")
const equipmentJournalUpdateNoteFailed = createAction("equipmentJournal/updateNoteFailed")

// Actions for fetching trip locations
const equipmentTripLocationsRequested = createAction("equipmentJournal/tripLocationsRequested")
const equipmentTripLocationsReceived = createAction("equipmentJournal/tripLocationsReceived")
const equipmentTripLocationsFailed = createAction("equipmentJournal/tripLocationsFailed")

// Actions for fetching journal versions
const equipmentJournalVersionsRequested = createAction("equipmentJournal/versionsRequested")
const equipmentJournalVersionsReceived = createAction("equipmentJournal/versionsReceived")
const equipmentJournalVersionsFailed = createAction("equipmentJournal/versionsFailed")

// API action creators - Using unified endpoint with boat_id parameter
export const getEquipmentJournalNote = (equipmentId, date) =>
  api({
    url: `/guide/journal_notes?date=${date}&boat_id=${equipmentId}`,
    onStart: equipmentJournalNoteRequested.type,
    onSuccess: equipmentJournalNoteReceived.type,
    onError: equipmentJournalNoteFailed.type
  })

export const createEquipmentJournal = (equipmentId, data) =>
  api({
    url: `/guide/journal_notes?boat_id=${equipmentId}`,
    method: "post",
    onStart: equipmentJournalCreateNoteRequested.type,
    onSuccess: equipmentJournalCreateNoteReceived.type,
    onError: equipmentJournalCreateNoteFailed.type,
    data
  })

export const updateEquipmentJournal = (equipmentId, id, data) =>
  api({
    url: `/guide/journal_notes/${id}?boat_id=${equipmentId}`,
    method: "put",
    data: data,
    onStart: equipmentJournalUpdateNoteRequested.type,
    onSuccess: equipmentJournalUpdateNoteReceived.type,
    onError: equipmentJournalUpdateNoteFailed.type
  })

export const saveEquipmentJournal = (equipmentId, id, data) =>
  id ? updateEquipmentJournal(equipmentId, id, data) : createEquipmentJournal(equipmentId, data)

export const getEquipmentTripLocations = () =>
  api({
    url: "/guide/calendar/guide_trip_locations",
    onStart: equipmentTripLocationsRequested.type,
    onSuccess: equipmentTripLocationsReceived.type,
    onError: equipmentTripLocationsFailed.type
  })

export const getEquipmentJournalVersions = (id, params = {}) =>
  api({
    url: `/guide/journal_notes/${id}/versions`,
    params: { ...params },
    onStart: equipmentJournalVersionsRequested.type,
    onSuccess: equipmentJournalVersionsReceived.type,
    onError: equipmentJournalVersionsFailed.type
  })

// Initial state
const initialState = {
  journalNote: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  tripLocations: {
    ...LOADING_INITIAL_STATE,
    data: []
  },
  journalVersions: {
    ...LOADING_INITIAL_STATE,
    data: {
      versions: [],
      pagination: null
    }
  }
}

// Reducer
const equipmentJournalReducer = createReducer(initialState, (builder) => {
  builder
    // Fetch journal note
    .addCase(equipmentJournalNoteRequested, (state) => {
      setRequested(state.journalNote)
      state.journalNote.data = null
    })
    .addCase(equipmentJournalNoteReceived, (state, action) => {
      setReceived(state.journalNote)
      state.journalNote.data = action.payload || null
    })
    .addCase(equipmentJournalNoteFailed, (state) => {
      setFailed(state.journalNote)
      state.journalNote.data = null
    })
    // Create journal note
    .addCase(equipmentJournalCreateNoteRequested, (state) => {
      setRequested(state.journalNote)
    })
    .addCase(equipmentJournalCreateNoteReceived, (state, action) => {
      setReceived(state.journalNote)
      state.journalNote.data = action.payload
    })
    .addCase(equipmentJournalCreateNoteFailed, (state) => {
      setFailed(state.journalNote)
    })
    // Update journal note
    .addCase(equipmentJournalUpdateNoteRequested, (state) => {
      setRequested(state.journalNote)
    })
    .addCase(equipmentJournalUpdateNoteReceived, (state, action) => {
      setReceived(state.journalNote)
      state.journalNote.data = action.payload
    })
    .addCase(equipmentJournalUpdateNoteFailed, (state) => {
      setFailed(state.journalNote)
    })
    // Fetch trip locations
    .addCase(equipmentTripLocationsRequested, (state) => {
      setRequested(state.tripLocations)
      state.tripLocations.data = []
    })
    .addCase(equipmentTripLocationsReceived, (state, action) => {
      setReceived(state.tripLocations)
      state.tripLocations.data = action.payload?.trip_locations || []
    })
    .addCase(equipmentTripLocationsFailed, (state) => {
      setFailed(state.tripLocations)
      state.tripLocations.data = []
    })
    // Fetch journal versions
    .addCase(equipmentJournalVersionsRequested, (state) => {
      setRequested(state.journalVersions)
    })
    .addCase(equipmentJournalVersionsReceived, (state, action) => {
      setReceived(state.journalVersions)
      state.journalVersions.data = {
        versions: action.payload?.versions || [],
        pagination: action.payload?.pagination || null
      }
    })
    .addCase(equipmentJournalVersionsFailed, (state) => {
      setFailed(state.journalVersions)
      state.journalVersions.data = {
        versions: [],
        pagination: null
      }
    })
})

export default equipmentJournalReducer
